import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Undo2, Eye, Search, Loader2, Clock, ShieldAlert } from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); } catch (e) { return iso || ""; } };
const RES_LABEL = { student: "cadastro de aluno", user: "login", enrollments: "matrícula(s)", group_ids: "turma(s)", instructor: "cadastro de professor", groups_person: "turma(s)", course: "curso",
  questions: "questão(ões)", exam_attempts: "tentativa(s) de prova", lesson_reactions: "reação(ões)", lesson_comments: "comentário(s)", lesson_notes: "anotação(ões)",
  module: "módulo", lesson: "aula", enrollment: "matrícula", doc: "registro", charge: "cobrança", order: "pedido", certificate: "certificado", attempts: "tentativa(s) de prova" };
const resumoTxt = (r) => Object.entries(r || {}).filter(([k]) => RES_LABEL[k]).map(([k, n]) => (typeof n === "number" && n > 1 ? `${n} ${RES_LABEL[k]}` : (n === 1 && /\(s\)|\(ões\)/.test(RES_LABEL[k]) ? `1 ${RES_LABEL[k]}` : RES_LABEL[k]))).join(" · ");
const RETENTION = [[0, "Até eu apagar"], [30, "30 dias"], [60, "60 dias"], [90, "90 dias"], [180, "6 meses"], [365, "1 ano"]];

export default function Trash() {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [view, setView] = useState(null);
  const [confirm, setConfirm] = useState(null);   // {type: "restore"|"purge", item}

  const load = useCallback(() => {
    api.get("/admin/trash", { params: { kind, q, limit: 100 } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [kind, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const act = async () => {
    const { type, item } = confirm;
    setBusy(item.id);
    try {
      if (type === "restore") { await api.post(`/admin/trash/${item.id}/restore`); toast.success(`${item.label} foi restaurado.`); }
      else { await api.delete(`/admin/trash/${item.id}`); toast.success(`${item.label} foi apagado de vez.`); }
      setConfirm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const openView = async (item) => {
    try { setView((await api.get(`/admin/trash/${item.id}`)).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setRet = async (days) => {
    try { await api.put("/admin/trash/settings", { retention_days: Number(days) }); toast.success(Number(days) ? `Itens ficam ${days} dias na lixeira.` : "Itens ficam na lixeira até você apagar."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const kinds = data?.kinds || {};
  const counts = data?.counts || {};
  const totalAll = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="trash-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Trash2 className="h-6 w-6 text-[#1E9E2A]" /> Lixeira</h1>
          <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">Tudo que é excluído no painel fica guardado aqui, com todos os dados, e pode ser restaurado. Se alguém tentar cadastrar um e-mail ou CPF que está na lixeira, o sistema avisa para você apagar de vez (ou restaurar) antes.</p>
        </div>
        <label className="text-sm text-slate-600 flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-400" /> Guardar por
          <select value={data?.cfg?.retention_days ?? 0} onChange={(e) => setRet(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="trash-retention">
            {RETENTION.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setKind("")} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${kind === "" ? "bg-[#232D4F] text-white border-[#232D4F]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>Tudo ({totalAll})</button>
        {Object.entries(counts).map(([k, n]) => (
          <button key={k} onClick={() => setKind(k)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${kind === k ? "bg-[#232D4F] text-white border-[#232D4F]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{kinds[k] || k} ({n})</button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, e-mail ou CPF" className="pl-9" data-testid="trash-search" />
        </div>
      </div>

      {!data ? <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div> : data.items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">
          <Trash2 className="h-8 w-8 mx-auto text-slate-300" />
          <p className="mt-2 font-semibold text-slate-600">A lixeira está vazia</p>
          <p className="text-sm">Quando você excluir um aluno, professor, curso, turma ou pedido, ele aparece aqui.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
          {data.items.map((it) => (
            <div key={it.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`trash-item-${it.id}`}>
              <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded-md bg-slate-100 text-slate-600 shrink-0">{it.kind_label}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{it.label}</div>
                <div className="text-xs text-slate-500 truncate">{it.email ? `${it.email} · ` : ""}excluído em {fmtDate(it.deleted_at)}{it.deleted_by?.name ? ` por ${it.deleted_by.name}` : ""}</div>
                <div className="text-xs text-slate-400 truncate">{resumoTxt(it.resumo)}{it.purge_at ? ` · some em ${fmtDate(it.purge_at)}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => openView(it)}><Eye className="h-4 w-4 mr-1" /> Ver</Button>
                <Button size="sm" className="text-white" style={{ background: "#1E9E2A" }} onClick={() => setConfirm({ type: "restore", item: it })} disabled={busy === it.id}><Undo2 className="h-4 w-4 mr-1" /> Restaurar</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirm({ type: "purge", item: it })} disabled={busy === it.id}><Trash2 className="h-4 w-4 mr-1" /> Apagar de vez</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{view?.label}</DialogTitle><DialogDescription>{view?.kind_label} · excluído em {fmtDate(view?.deleted_at)}. Dados sensíveis (senhas, tokens, PDFs) aparecem ocultos.</DialogDescription></DialogHeader>
          <pre className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-[55vh] overflow-auto whitespace-pre-wrap break-words">{view ? JSON.stringify(view.payload, null, 2) : ""}</pre>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{confirm?.type === "purge" && <ShieldAlert className="h-5 w-5 text-red-600" />} {confirm?.type === "restore" ? "Restaurar item?" : "Apagar de vez?"}</DialogTitle>
            <DialogDescription>
              {confirm?.type === "restore"
                ? `"${confirm?.item.label}" volta para o sistema com os mesmos dados.`
                : `"${confirm?.item.label}" será apagado definitivamente da lixeira. Depois disso não dá para recuperar.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancelar</Button>
            <Button onClick={act} disabled={!!busy} className="text-white" style={{ background: confirm?.type === "purge" ? "#DC2626" : "#1E9E2A" }} data-testid="trash-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirm?.type === "restore" ? "Restaurar" : "Apagar de vez"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
